package com.klantenstop.web.components.layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.klantenstop.web.auth.AuthModule
import com.klantenstop.web.components.LandingHeader
import com.klantenstop.web.components.NavigationItems
import com.klantenstop.web.navigation.logout
import com.klantenstop.web.pages.sections.FooterSection
import com.klantenstop.web.utils.AnalyticsManager
import com.klantenstop.web.utils.Res
import com.klantenstop.web.utils.SiteGlobals
import com.klantenstop.web.utils.UpdatePageSeo
import com.klantenstop.web.utils.format
import com.klantenstop.web.utils.logoImage
import com.materialkobweb.components.DsPageLayout
import com.materialkobweb.components.sections.OverflowMenu
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.px

private const val FALLBACK_DESCRIPTION =
    "KlantenStop — vind snel huisartsen, tandartsen en andere dienstverleners die nog nieuwe patiënten of klanten aannemen."

@Composable
fun PageLayout(
    modifier: Modifier = Modifier,
    title: String,
    seo: PageSeoConfig = PageSeoConfig.Managed(description = "", indexable = false),
    canonicalPath: String? = null,
    loginRequired: Boolean = false,
    showMenuItems: Boolean = true,
    content: @Composable () -> Unit
) {
    val authStatus = AuthModule.authStatus.collectAsState()
    val isAuthenticated = authStatus.value?.authenticated

    if (!SiteGlobals.isExporting && loginRequired && isAuthenticated == false) {
        // Login is required but tokens are not valid — redirect to the login page
        logout()
        return
    }

    val breakpoint = rememberBreakpoint()
    var overflowMenuOpened by remember { mutableStateOf(false) }

    // Track page views automatically on every route change.
    val ctx = rememberPageContext()
    LaunchedEffect(ctx.route.path) {
        AnalyticsManager.trackPageView(ctx.route.path)
    }

    // Update SEO meta tags on every navigation, unless the page manages its own (External).
    // loginRequired acts as a hard override: auth-gated pages are never indexable even if
    // the caller mistakenly passes indexable = true.
    if (seo is PageSeoConfig.Managed) {
        val resolvedCanonicalPath = canonicalPath ?: if (ctx.route.path == "/index") "/" else ctx.route.path.trimEnd('/').ifEmpty { "/" }
        UpdatePageSeo(
            title = Res.String.DocumentTitle.format(title),
            description = seo.description.ifBlank { FALLBACK_DESCRIPTION },
            canonicalPath = resolvedCanonicalPath,
            indexable = seo.indexable && !loginRequired,
            keywords = seo.keywords,
            breadcrumbLabel = seo.breadcrumbLabel,
        )
    }

    LaunchedEffect(breakpoint) {
        if (breakpoint <= Breakpoint.MD) return@LaunchedEffect
        overflowMenuOpened = false
    }

    DsPageLayout(
        modifier = modifier,
        title = Res.String.DocumentTitle.format(title),
        header = {
            LandingHeader(showMenuItems) {
                overflowMenuOpened = true
            }
        },
        overflowMenu = {
            OverflowMenu(
                logoImage = logoImage(ColorMode.current),
                onMenuClosed = { overflowMenuOpened = false }
            ) { closeMenu ->
                Column(
                    verticalArrangement = Arrangement.spacedBy(10.px)
                ) {
                    NavigationItems {
                        closeMenu()
                    }
                }
            }
        },
        overflowMenuOpened = overflowMenuOpened,
        footer = {
            FooterSection(showMenuItems)
        },
        content = {
            content()
        }
    )
}

/**
 * Controls how [PageLayout] updates the document's SEO meta tags.
 *
 * - [Managed]: PageLayout calls [UpdatePageSeo] using the supplied [description] and
 *   [indexable] flag. Use this for all regular public or private pages.
 * - [External]: PageLayout skips the generic meta tag update entirely. Use this when
 *   the page manages its own SEO (e.g. company detail pages that call [UpdateCompanySeo]).
 *   The page is then responsible for keeping meta tags up-to-date across navigation.
 */
sealed class PageSeoConfig {
    data class Managed(
        val description: String,
        val indexable: Boolean = true,
        val keywords: List<String> = emptyList(),
        /**
         * Short, user-facing label for the current page's breadcrumb crumb (e.g. "Zoeken",
         * "Contact"). When set, a two-level `BreadcrumbList` JSON-LD (Home › [breadcrumbLabel])
         * is injected alongside the page's other structured data. Leave null for pages that
         * don't warrant a breadcrumb (e.g. Home itself, or auth-gated pages).
         */
        val breadcrumbLabel: String? = null,
    ) : PageSeoConfig()

    object External : PageSeoConfig()
}
